import React, { FC, useCallback, useEffect, useRef } from "react";
import { Renderer, Stave, StaveNote, Accidental, Formatter, Dot, StaveTie, Curve, Barline } from "vexflow";

export interface DerivedNote {
  pitch: string;   // e.g. "C", "F#", "Bb"
  octave: number;  // e.g. 4
  duration: string; // VexFlow duration code: "w" | "h" | "q" | "8" | "16" | "32"
  dotted: boolean;  // whether the note carries a dot
}

interface StaveRendererProps {
  notes: DerivedNote[];
  clef?: string;
  timeSignature?: string;
  keySignature?: string;
  /** Pairs of note indices [from, to] connected by a tie */
  ties?: Array<[number, number]>;
  /** Pairs of note indices [from, to] connected by a slur */
  slurs?: Array<[number, number]>;
  /** Note indices before which a barline should be drawn (bar boundaries) */
  barBreaks?: number[];
}

const NATURAL_TO_VEX: Record<string, string> = {
  C: "c", D: "d", E: "e", F: "f", G: "g", A: "a", B: "b",
};

/** Number of accidentals in a key signature (for reserving stave width). */
const KEYSIG_ACCIDENTALS: Record<string, number> = {
  C: 0, G: 1, D: 2, A: 3, E: 4, B: 5, "F#": 6, "C#": 7,
  F: 1, Bb: 2, Eb: 3, Ab: 4, Db: 5, Gb: 6, Cb: 7,
  Am: 0, Em: 1, Bm: 2, "F#m": 3, "C#m": 4, "G#m": 5, "D#m": 6, "A#m": 7,
  Dm: 1, Gm: 2, Cm: 3, Fm: 4, Bbm: 5, Ebm: 6, Abm: 7,
};

const BARS_PER_LINE = 4;

/**
 * Parse a pitch string like "C", "F#", "Bb", "Ab", "A#"
 * into a VexFlow key string and optional accidental symbol.
 */
function parsePitch(
  pitch: string,
  octave: number
): { key: string; accidental?: string } {
  const base = pitch[0].toUpperCase();
  const modifier = pitch.slice(1).toLowerCase();
  const vexBase = NATURAL_TO_VEX[base] ?? base.toLowerCase();

  if (!modifier) return { key: `${vexBase}/${octave}` };
  if (modifier === "#") return { key: `${vexBase}/${octave}`, accidental: "#" };
  if (modifier === "b" || modifier === "flat")
    return { key: `${vexBase}/${octave}`, accidental: "b" };
  return { key: `${vexBase}/${octave}` };
}

const StaveRenderer: FC<StaveRendererProps> = ({
  notes,
  clef = "treble",
  timeSignature,
  keySignature,
  ties,
  slurs,
  barBreaks,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const wrapperRef   = useRef<HTMLDivElement>(null);

  const draw = useCallback(
    (availableWidth: number) => {
      const el = containerRef.current;
      if (!el) return;
      el.innerHTML = "";

      const LEFT = 10;
      const RIGHT = 10;
      const TOP = 15;
      const LINE_HEIGHT = 110;

      const accidentals = keySignature ? (KEYSIG_ACCIDENTALS[keySignature] ?? 0) : 0;
      const clefKeyExtra = 34 + accidentals * 9 + (timeSignature ? 24 : 0);

      // ── Empty stave (e.g. key-signature quiz): just clef + key signature ──
      if (notes.length === 0) {
        const totalWidth = Math.max(260, availableWidth);
        const renderer = new Renderer(el, Renderer.Backends.SVG);
        renderer.resize(totalWidth, 130);
        const context = renderer.getContext();
        const stave = new Stave(LEFT, 20, totalWidth - LEFT - RIGHT);
        stave.addClef(clef);
        if (keySignature && keySignature !== "C") stave.addKeySignature(keySignature);
        if (timeSignature) stave.addTimeSignature(timeSignature);
        stave.setContext(context).draw();
        return;
      }

      // ── Build every StaveNote once, indexed by original note index ──
      const vexNotes = notes.map((noteData) => {
        const { key, accidental } = parsePitch(noteData.pitch, noteData.octave);
        const sn = new StaveNote({ clef, keys: [key], duration: noteData.duration, autoStem: true });
        if (accidental) sn.addModifier(new Accidental(accidental), 0);
        if (noteData.dotted) Dot.buildAndAttach([sn]);
        return sn;
      });

      // ── Split notes into bars ──
      const barStarts = [0, ...(barBreaks ?? [])];
      const barEnds = [...(barBreaks ?? []), notes.length];
      const numBars = barStarts.length;

      // ── Group bars into lines of BARS_PER_LINE ──
      const lines: number[][] = [];
      for (let b = 0; b < numBars; b += BARS_PER_LINE) {
        const line: number[] = [];
        for (let k = b; k < Math.min(b + BARS_PER_LINE, numBars); k++) line.push(k);
        lines.push(line);
      }

      // ── Bar width: bars fill the line; wrapping lines use full BARS_PER_LINE divisor ──
      const divisor = numBars <= BARS_PER_LINE ? numBars : BARS_PER_LINE;
      const minTotal = clefKeyExtra + divisor * 130 + LEFT + RIGHT;
      const totalWidth = Math.max(availableWidth, minTotal);
      const lineContentWidth = totalWidth - LEFT - RIGHT;
      const baseBarWidth = (lineContentWidth - clefKeyExtra) / divisor;

      const renderer = new Renderer(el, Renderer.Backends.SVG);
      renderer.resize(totalWidth, TOP + lines.length * LINE_HEIGHT + 25);
      const context = renderer.getContext();

      // Track which line each note index sits on (to skip cross-line ties/slurs)
      const noteLine: number[] = new Array(notes.length).fill(0);

      lines.forEach((barIdxs, lineIdx) => {
        const y = TOP + lineIdx * LINE_HEIGHT;
        let x = LEFT;

        barIdxs.forEach((barIdx, posInLine) => {
          const isFirstOfLine = posInLine === 0;
          const isVeryFirstBar = barIdx === 0;
          const isLastBar = barIdx === numBars - 1;

          const width = (isFirstOfLine ? clefKeyExtra : 0) + baseBarWidth;
          const stave = new Stave(x, y, width);

          if (isFirstOfLine) {
            stave.addClef(clef);
            if (keySignature && keySignature !== "C") stave.addKeySignature(keySignature);
          } else {
            stave.setBegBarType(Barline.type.NONE);
          }
          if (isVeryFirstBar && timeSignature) stave.addTimeSignature(timeSignature);
          if (isLastBar) stave.setEndBarType(Barline.type.END);

          stave.setContext(context).draw();

          const barNotes = vexNotes.slice(barStarts[barIdx], barEnds[barIdx]);
          for (let i = barStarts[barIdx]; i < barEnds[barIdx]; i++) noteLine[i] = lineIdx;
          if (barNotes.length > 0) {
            Formatter.FormatAndDraw(context, stave, barNotes);
          }

          x += width;
        });
      });

      // ── Ties (same pitch) — only when both notes are on the same line ──
      (ties ?? []).forEach(([from, to]) => {
        if (vexNotes[from] && vexNotes[to] && noteLine[from] === noteLine[to]) {
          new StaveTie({
            firstNote: vexNotes[from],
            lastNote: vexNotes[to],
            firstIndexes: [0],
            lastIndexes: [0],
          })
            .setContext(context)
            .draw();
        }
      });

      // ── Slurs (phrasing) — only within the same line ──
      (slurs ?? []).forEach(([from, to]) => {
        if (vexNotes[from] && vexNotes[to] && noteLine[from] === noteLine[to]) {
          new Curve(vexNotes[from], vexNotes[to], {}).setContext(context).draw();
        }
      });
    },
    [notes, clef, timeSignature, keySignature, ties, slurs, barBreaks]
  );

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    // Initial render at the current container width
    draw(wrapper.offsetWidth || 460);

    // Re-render whenever the container is resized (orientation change, etc.)
    const ro = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width) draw(width);
    });
    ro.observe(wrapper);
    return () => ro.disconnect();
  }, [draw]);

  return (
    <div ref={wrapperRef} style={{ width: "100%" }}>
      <div ref={containerRef} />
    </div>
  );
};

export default StaveRenderer;
